/**
 * 作品数据聚合服务 — V9 我的作品集系统
 *
 * 负责：
 *   1. 从 project-store 和 works-store 聚合数据
 *   2. 应用筛选和排序
 *   3. 提供聚合查询接口
 */

import { listProjects } from "../storage/project-store";
import {
  listMyProjects,
  listForgeProjects,
  sortByLatest,
  type MyProject,
  type ForgeProject,
  type Work,
  type WorkSource,
  type BattleType,
} from "../storage/works-store";

// ============================================================
// 类型定义
// ============================================================

export interface AggregatedWork {
  work: Work;
  /** 用于显示的标题 */
  title: string;
  /** 用于显示的描述 */
  description: string;
  /** 用于显示的时间戳 */
  timestamp: number;
  /** 用于显示的标签 */
  tags: string[];
  /** 铸剑阁关卡难度 1-5（仅 forge 项目有效） */
  difficulty?: number;
  /** 铸剑阁关卡评分 S/A/B/C（仅 forge 项目有效） */
  score?: string;
}

export interface FilterOptions {
  source?: WorkSource | "all";
  type?: BattleType | "all";
}

// ============================================================
// 数据聚合
// ============================================================

/** 将 Work 转换为 AggregatedWork */
function aggregateWork(work: Work): AggregatedWork {
  if (work.source === "editor") {
    const myProject = work as MyProject;
    return {
      work,
      title: myProject.name,
      description: myProject.description || "无描述",
      timestamp: myProject.updatedAt,
      tags: myProject.tags || [],
    };
  } else {
    const forgeProject = work as ForgeProject;
    const stateLabel: Record<string, string> = {
      completed: "已完成",
      current: "进行中",
      locked: "未解锁",
    };
    return {
      work,
      title: forgeProject.levelName,
      description: `第 ${forgeProject.chapterNum} 章 · ${stateLabel[forgeProject.state] || "未知"}`,
      timestamp: forgeProject.completedAt || 0,
      tags: [],
      difficulty: forgeProject.difficulty,
      score: forgeProject.score,
    };
  }
}

/** 获取聚合后的所有作品 */
export function getAggregatedWorks(options: FilterOptions = {}): AggregatedWork[] {
  const { source = "all", type = "all" } = options;

  let works: Work[] = [];

  if (source === "all") {
    // 合并两类作品
    const myProjects = listMyProjects();
    const forgeProjects = listForgeProjects();
    works = [...myProjects, ...forgeProjects];
  } else if (source === "editor") {
    works = listMyProjects();
    if (type !== "all") {
      works = works.filter((w) => (w as MyProject).type === type);
    }
  } else if (source === "forge") {
    works = listForgeProjects();
  }

  // 排序（最新优先）
  works = sortByLatest(works);

  // 聚合
  return works.map(aggregateWork);
}

/** 获取统计信息 */
export function getWorkStats(): {
  totalWorks: number;
  myProjectsCount: number;
  forgeProjectsCount: number;
  typeDistribution: Record<BattleType, number>;
} {
  const myProjects = listMyProjects();
  const forgeProjects = listForgeProjects();

  const typeDistribution: Record<BattleType, number> = {
    "1v1": 0,
    "2v2": 0,
    "3v3": 0,
  };

  myProjects.forEach((p) => {
    typeDistribution[p.type]++;
  });

  return {
    totalWorks: myProjects.length + forgeProjects.length,
    myProjectsCount: myProjects.length,
    forgeProjectsCount: forgeProjects.length,
    typeDistribution,
  };
}

/** 搜索作品（按名称） */
export function searchWorks(query: string, options: FilterOptions = {}): AggregatedWork[] {
  const works = getAggregatedWorks(options);
  const lowerQuery = query.toLowerCase();
  return works.filter((w) => w.title.toLowerCase().includes(lowerQuery));
}

// ============================================================
// 与编辑器项目的同步
// ============================================================

/** 从 project-store 同步我的作品到 works-store
 *
 * 用途：
 *   - 屏 2 编辑器保存项目时调用
 *   - 屏 13 初始化时调用（确保数据一致）
 *
 * 策略：
 *   - 遍历 project-store 中的所有项目
 *   - 如果项目不在 works-store 中，添加
 *   - 如果项目已在 works-store 中，更新
 */
export function syncProjectsToWorks(): void {
  const projects = listProjects();
  const myProjects = listMyProjects();

  projects.forEach((project) => {
    // 跳过没有 code 的项目
    if (!project.code) return;

    // 检查是否已在 works-store 中
    const existing = myProjects.find((p) => p.id === project.id);

    if (!existing) {
      // 新项目，添加到 works-store
      // 注意：这里假设 project.id 可以直接用作 MyProject.id
      // 如果需要不同的 ID 策略，可以修改
      const { tank_codes, strategy_doc } = project;
      const code = tank_codes?.[0] || project.code;

      // 从项目名推断类型（简单启发式）
      let type: BattleType = "1v1";
      if (strategy_doc) {
        // 如果有战略文档，说明是多坦克项目
        type = tank_codes && tank_codes.length === 2 ? "2v2" : "3v3";
      }

      // 这里我们不直接调用 addMyProject，因为 project 已经有 id/createdAt
      // 而是直接更新 works-store
      const { myProjects: stored } = require("../storage/works-store").getAllWorks();
      stored.push({
        source: "editor",
        id: project.id,
        name: project.name,
        code,
        createdAt: project.createdAt,
        updatedAt: project.updatedAt,
        type,
        projectType: project.type,
        exampleId: project.exampleId,
      });
      require("../storage/works-store").saveAllWorks({ myProjects: stored, forgeProjects: [] });
    } else {
      // 现有项目，更新
      const code = project.tank_codes?.[0] || project.code;
      let type: BattleType = "1v1";
      if (project.strategy_doc) {
        type = project.tank_codes && project.tank_codes.length === 2 ? "2v2" : "3v3";
      }

      const { myProjects: stored } = require("../storage/works-store").getAllWorks();
      const idx = stored.findIndex((p: MyProject) => p.id === project.id);
      if (idx !== -1) {
        stored[idx] = {
          ...stored[idx],
          name: project.name,
          code,
          updatedAt: project.updatedAt,
          type,
          projectType: project.type,
          exampleId: project.exampleId,
        };
        require("../storage/works-store").saveAllWorks({ myProjects: stored, forgeProjects: [] });
      }
    }
  });
}
